import { Injectable } from '@angular/core';
import { CitationInfo } from '../advisor_new/message-list/citation-group/citation-group.component';
import { SourceDocument } from 'src/app/models/advisor.interface';

export type ContentSegment =
  | { type: 'text'; content: string }
  | { type: 'citation'; citation: CitationInfo }
  | { type: 'citationGroup'; citations: CitationInfo[] }
  | { type: 'blockWithCitations'; content: string; citationNumbers: number[] };

export interface ParsedContent {
  segments: ContentSegment[];
}

interface CitationMatch {
  fullMatch: string;
  numbers: number[];
  startIndex: number;
  endIndex: number;
}

@Injectable({
  providedIn: 'root'
})
export class MessageRendererService {
  /**
   * Transform message text with [n] citations into structured content
   */
  transformMessage(messageText: string, sourceDocuments: SourceDocument[] | null): ParsedContent {
    if (!messageText) {
      return { segments: [] };
    }

    // Convert literal \n strings to actual newlines
    messageText = messageText.replace(/\\n/g, '\n');

    if (!sourceDocuments || sourceDocuments.length === 0) {
      return { segments: [{ type: 'text', content: messageText }] };
    }

    // Simple check: if text has block-level markdown, keep it intact
    if (this.hasBlockLevelMarkdown(messageText)) {
      const citationNumbers = this.extractAllCitationNumbers(messageText);

      // Wrap citations in styled spans with tooltips before passing to markdown
      const wrappedContent = this.wrapCitationsInSpans(messageText, sourceDocuments);

      return {
        segments: [
          {
            type: 'blockWithCitations',
            content: wrappedContent,
            citationNumbers
          }
        ]
      };
    }

    // For simple inline text, split citations normally
    const citationMatches = this.findCitationGroups(messageText);

    if (citationMatches.length === 0) {
      return { segments: [{ type: 'text', content: messageText }] };
    }

    const segments: ContentSegment[] = [];
    let lastIndex = 0;

    for (const match of citationMatches) {
      // Add text before this citation
      if (match.startIndex > lastIndex) {
        const textContent = messageText.substring(lastIndex, match.startIndex);
        if (textContent.trim()) {
          segments.push({ type: 'text', content: textContent });
        }
      }

      // Build citation info for each number in the match
      const citations = match.numbers.map(num => this.buildCitationInfo(num, sourceDocuments));

      // Determine if this should be a group or individual citations
      if (citations.length >= 3) {
        segments.push({
          type: 'citationGroup',
          citations
        });
      } else {
        citations.forEach(citation => {
          segments.push({
            type: 'citation',
            citation
          });
        });
      }

      lastIndex = match.endIndex;
    }

    // Add remaining text after last citation
    if (lastIndex < messageText.length) {
      const textContent = messageText.substring(lastIndex);
      if (textContent.trim()) {
        segments.push({ type: 'text', content: textContent });
      }
    }

    return { segments };
  }

  /**
   * Check if text contains block-level markdown that shouldn't be split
   * Only returns true for actual block structures (2+ consecutive list items, headings, etc.)
   */
  private hasBlockLevelMarkdown(text: string): boolean {
    // Check for headings
    if (/^#{1,6}\s/m.test(text)) return true;

    // Check for code blocks
    if (/```/.test(text)) return true;

    // Check for multiple paragraphs (2+ paragraph breaks)
    if ((text.match(/\n\s*\n/g) || []).length >= 2) return true;

    // Check for consecutive list items (2+ in a row)
    // This prevents single inline citations like [1][2][3] from being treated as blocks
    const lines = text.split('\n');
    let consecutiveListLines = 0;

    for (const line of lines) {
      if (/^\s*[-*+]\s/.test(line) || /^\s*\d+\.\s/.test(line)) {
        consecutiveListLines++;
        if (consecutiveListLines >= 2) {
          return true; // Found 2+ consecutive list items
        }
      } else if (line.trim() !== '') {
        // Non-empty, non-list line resets counter
        consecutiveListLines = 0;
      }
      // Empty lines don't reset counter (allow spacing in lists)
    }

    return false;
  }

  /**
   * Extract all citation numbers from text
   */
  private extractAllCitationNumbers(text: string): number[] {
    const numbers: number[] = [];

    // Match range citations [1-5]
    const rangeRegex = /\[(\d+)-(\d+)\]/g;
    let match: RegExpExecArray | null;

    while ((match = rangeRegex.exec(text)) !== null) {
      const start = parseInt(match[1], 10);
      const end = parseInt(match[2], 10);
      for (let i = start; i <= end; i++) {
        if (!numbers.includes(i)) numbers.push(i);
      }
    }

    // Match comma-separated [1,2,3]
    const commaRegex = /\[(\d+(?:,\s*\d+)+)\]/g;
    while ((match = commaRegex.exec(text)) !== null) {
      const nums = match[1].split(',').map(n => parseInt(n.trim(), 10));
      nums.forEach(n => {
        if (!numbers.includes(n)) numbers.push(n);
      });
    }

    // Match single citations [1]
    const singleRegex = /\[(\d+)\]/g;
    while ((match = singleRegex.exec(text)) !== null) {
      const num = parseInt(match[1], 10);
      if (!numbers.includes(num)) numbers.push(num);
    }

    return numbers.sort((a, b) => a - b);
  }

  /**
   * Find all citation patterns in text, grouping consecutive citations
   */
  private findCitationGroups(text: string): CitationMatch[] {
    const matches: CitationMatch[] = [];

    // Pattern 1: Range citations like [1-3]
    const rangePattern = /\[(\d+)-(\d+)\]/g;

    // Pattern 2: Comma-separated citations like [1,2,3]
    const commaSeparatedPattern = /\[(\d+(?:,\s*\d+)+)\]/g;

    // Pattern 3: Consecutive bracket citations like [1][2][3]
    const consecutivePattern = /(\[\d+\]){2,}/g;

    // Pattern 4: Single citations like [1]
    const singlePattern = /\[\d+\]/g;

    // First find range citations
    let match: RegExpExecArray | null;
    while ((match = rangePattern.exec(text)) !== null) {
      const fullMatch = match[0];
      const startIndex = match.index;
      const endIndex = startIndex + fullMatch.length;

      const start = parseInt(match[1], 10);
      const end = parseInt(match[2], 10);
      const numbers: number[] = [];

      for (let i = start; i <= end; i++) {
        numbers.push(i);
      }

      matches.push({
        fullMatch,
        numbers,
        startIndex,
        endIndex
      });
    }

    // Find comma-separated citations
    while ((match = commaSeparatedPattern.exec(text)) !== null) {
      const fullMatch = match[0];
      const startIndex = match.index;
      const endIndex = startIndex + fullMatch.length;

      const isOverlapping = matches.some(
        m =>
          (startIndex >= m.startIndex && startIndex < m.endIndex) || (endIndex > m.startIndex && endIndex <= m.endIndex)
      );

      if (!isOverlapping) {
        const numbersString = match[1];
        const numbers = numbersString.split(',').map(n => parseInt(n.trim(), 10));

        matches.push({
          fullMatch,
          numbers,
          startIndex,
          endIndex
        });
      }
    }

    // Find consecutive bracket citations
    while ((match = consecutivePattern.exec(text)) !== null) {
      const fullMatch = match[0];
      const startIndex = match.index;
      const endIndex = startIndex + fullMatch.length;

      const isOverlapping = matches.some(
        m =>
          (startIndex >= m.startIndex && startIndex < m.endIndex) || (endIndex > m.startIndex && endIndex <= m.endIndex)
      );

      if (!isOverlapping) {
        const numbers = this.extractNumbers(fullMatch);
        matches.push({
          fullMatch,
          numbers,
          startIndex,
          endIndex
        });
      }
    }

    // Finally, find single citations
    while ((match = singlePattern.exec(text)) !== null) {
      const startIndex = match.index;
      const endIndex = startIndex + match[0].length;

      const isOverlapping = matches.some(m => startIndex >= m.startIndex && endIndex <= m.endIndex);

      if (!isOverlapping) {
        const numbers = this.extractNumbers(match[0]);
        matches.push({
          fullMatch: match[0],
          numbers,
          startIndex,
          endIndex
        });
      }
    }

    return matches.sort((a, b) => a.startIndex - b.startIndex);
  }

  /**
   * Extract all numbers from a string like "[1][2][3]" -> [1, 2, 3]
   */
  private extractNumbers(text: string): number[] {
    const numberPattern = /\d+/g;
    const numbers: number[] = [];
    let match: RegExpExecArray | null;

    while ((match = numberPattern.exec(text)) !== null) {
      numbers.push(parseInt(match[0], 10));
    }

    return numbers;
  }

  /**
   * Wrap citation patterns [n] in styled spans with tooltips for rendering
   * Also handles grouping of consecutive citations [1][2][3] -> (1)(...)
   */
  private wrapCitationsInSpans(text: string, sourceDocuments: SourceDocument[]): string {
    // First, handle groups of 3+ consecutive citations
    text = this.replaceCitationGroups(text, sourceDocuments);

    // Then handle remaining individual citations
    text = text.replace(/\[(\d+)\]/g, (match, num) => {
      const citationNum = parseInt(num, 10);
      const doc = sourceDocuments.find(d => d.citation_id === citationNum);

      const tooltipText = doc?.metadata?.title || 'Source not available';
      const isAvailable = !!doc;
      const className = isAvailable ? 'citation-text' : 'citation-unavailable';

      // Escape quotes in tooltip text for HTML attribute
      const escapedTooltip = tooltipText.replace(/"/g, '&quot;');

      return `<span class="${className}" title="${escapedTooltip}">${num}</span>`;
    });

    return text;
  }

  /**
   * Replace groups of 3+ consecutive citations with collapsible group HTML
   */
  private replaceCitationGroups(text: string, sourceDocuments: SourceDocument[]): string {
    // Pattern for consecutive citations: [1][2][3] or [1,2,3] or [1-5]
    const consecutivePattern = /(\[\d+\]){3,}/g;
    const commaSeparatedPattern = /\[(\d+(?:,\s*\d+){2,})\]/g;
    const rangePattern = /\[(\d+)-(\d+)\]/g;

    // Handle range citations [1-5]
    text = text.replace(rangePattern, (match, start, end) => {
      const startNum = parseInt(start, 10);
      const endNum = parseInt(end, 10);
      const numbers: number[] = [];

      for (let i = startNum; i <= endNum; i++) {
        numbers.push(i);
      }

      if (numbers.length >= 3) {
        return this.createGroupSpanHTML(numbers, sourceDocuments);
      }
      return match; // Keep as-is if less than 3
    });

    // Handle comma-separated [1,2,3]
    text = text.replace(commaSeparatedPattern, (match, numsStr) => {
      const numbers = numsStr.split(',').map((n: string) => parseInt(n.trim(), 10));

      if (numbers.length >= 3) {
        return this.createGroupSpanHTML(numbers, sourceDocuments);
      }
      return match;
    });

    // Handle consecutive [1][2][3]
    text = text.replace(consecutivePattern, match => {
      const numbers = this.extractNumbers(match);

      if (numbers.length >= 3) {
        return this.createGroupSpanHTML(numbers, sourceDocuments);
      }
      return match;
    });

    return text;
  }

  /**
   * Create HTML for a collapsible citation group
   */
  private createGroupSpanHTML(numbers: number[], sourceDocuments: SourceDocument[]): string {
    const first = numbers[0];
    const rest = numbers.slice(1);

    const firstDoc = sourceDocuments.find(d => d.citation_id === first);
    const firstTitle = firstDoc?.metadata?.title || 'Source not available';
    const escapedFirstTitle = firstTitle.replace(/"/g, '&quot;');

    // Create collapsed group: (1)(...) that can expand to (1)(><)(2)(3)...
    const restHTML = rest
      .map(num => {
        const doc = sourceDocuments.find(d => d.citation_id === num);
        const title = doc?.metadata?.title || 'Source not available';
        const escapedTitle = title.replace(/"/g, '&quot;');
        const className = doc ? 'citation-text' : 'citation-unavailable';
        return `<span class="${className}" title="${escapedTitle}">${num}</span>`;
      })
      .join('');

    return (
      `<span class="citation-group-wrapper">` +
      `<span class="citation-text" title="${escapedFirstTitle}">${first}</span>` +
      `<span class="citation-expand-btn" data-group-citations="${numbers.join(',')}" title="Show ${rest.length} more citations">...</span>` +
      `<span class="citation-group-hidden" style="display:none;">` +
      `<span class="citation-collapse-btn" title="Hide citations">&gt;&lt;</span>${restHTML}` +
      `</span>` +
      `</span>`
    );
  }

  /**
   * Build citation info with tooltip and availability check
   */
  private buildCitationInfo(citationNumber: number, sourceDocuments: SourceDocument[]): CitationInfo {
    const doc = sourceDocuments.find(d => d.citation_id === citationNumber);

    return {
      number: citationNumber,
      tooltipText: doc?.metadata?.title || '',
      isAvailable: !!doc
    };
  }
}
